import React, {useEffect, useState} from 'react';
import {Checkbox, Switch, Box} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {Search as SearchIcon} from '@mui/icons-material';
import {ArrowRight} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import PaginationBar from '../../../../../components/PaginationBar';
import {useTrovoConfig} from '../../../../../utils/config';
import SampleRow from './SampleRow';
import {
    ActionBar,
    ActionItemStyled,
    ActionTitleStyled,
    ActionTextStyled,
    SelectedRowsTextStyled,
    FiltersContainer,
    FiltersWrapper,
    Search,
    SearchIconWrapper,
    StyledInputBase
} from './ExpandableTable.styles';

const ExpandableTable = (props: {formData: any; setFormData: any; handleNext: any; data: any; error: any; setError: any}) => {
    const navigate = useNavigate();
    const {formData, setFormData, handleNext, data, error, setError} = props;
    const {samples: selectedSamples} = formData;
    const [perPage, setPerPage] = useState(25);
    const [page, setPage] = useState(1);
    const [filtered, setFiltered] = useState(true);
    const {user} = useTrovoConfig();
    const [searchTerm, setSearchTerm] = useState('');
    const startIdx = (page - 1) * perPage;
    const endIdx = startIdx + perPage;
    const [displayedRows, setDisplayedRows] = useState([]);

    const handleSelectRow = (id: string) => {
        setFormData((prev: {name: string; samples: string[]}) => {
            if (prev.samples.includes(id)) {
                return {
                    ...prev,
                    samples: prev.samples.filter((item: string) => item !== id)
                };
            } else {
                return {
                    ...prev,
                    samples: [...prev.samples, id]
                };
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedSamples.length === displayedRows.length) {
            setFormData((prev: any) => ({
                ...prev,
                samples: []
            }));
        } else {
            setFormData((prev: {name: string; samples: string[]}) => {
                return {
                    ...prev,
                    samples: displayedRows.map(({id}: {id: any}) => id)
                };
            });
        }
    };

    useEffect(() => {
        selectedSamples.length && selectedSamples.length < 6 && !user.cb_item_price_id?.includes('demo')
            ? setError(`Please select at least ${6 - selectedSamples.length} more samples before proceeding.`)
            : setError('');
    }, [selectedSamples]);

    useEffect(() => {
        !error && selectedSamples.length > 5 ? handleNext(true) : handleNext(false);
    }, [selectedSamples, error]);

    useEffect(() => {
        searchTerm === ''
            ? setDisplayedRows(data)
            : setDisplayedRows(data?.filter((r: any) => r?.sampleName?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
    }, [searchTerm]);

    return (
        <>
            <ActionBar>
                <ActionItemStyled
                    sx={{cursor: 'pointer', bgcolor: 'surface-container-lowest'}}
                    onClick={() => navigate(`/samples/${data?.[0]?.experiment_id}`)}>
                    <div>
                        <ActionTitleStyled>Manage Samples</ActionTitleStyled>
                        <ActionTextStyled>Edit or review sample statuses.</ActionTextStyled>
                    </div>
                    <ArrowRight sx={{color: 'black'}} />
                </ActionItemStyled>
            </ActionBar>

            <Box sx={{bgcolor: 'surface-container-lowest', padding: '2%', borderRadius: '10px'}}>
                <FiltersContainer>
                    <FiltersWrapper>
                        <Search>
                            <SearchIconWrapper>
                                <SearchIcon />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search…"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                inputProps={{'aria-label': 'search'}}
                            />
                        </Search>
                    </FiltersWrapper>
                    <SelectedRowsTextStyled>
                        <b>{selectedSamples?.length}</b> of <b>{data?.length}</b> samples selected for analysis
                    </SelectedRowsTextStyled>
                </FiltersContainer>

                <TableContainer sx={{border: '1px solid', borderColor: 'outline', borderRadius: '5px'}}>
                    <Table aria-label="collapsible table">
                        <TableHead sx={{bgcolor: 'surface-container-high'}}>
                            <TableRow>
                                <TableCell width="10px" />
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedSamples.length === data?.length}
                                        onChange={handleSelectAll}
                                        disabled={data?.[0]?.experiment_id === '4015c7cc-1982-4bd4-ae1c-140a7aa6fba9'}
                                    />
                                </TableCell>
                                <TableCell align="left">SAMPLE NAME</TableCell>
                                <TableCell align="left">STATUS</TableCell>
                                {data?.[0] &&
                                    Object.keys(data[0]?.other_categories)?.map((c: any) => (
                                        <TableCell align="left">{c.toUpperCase()} </TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRows
                                ?.slice(startIdx, endIdx)
                                .map((row: any) => (
                                    <SampleRow
                                        key={row.id}
                                        row={row}
                                        selected={selectedSamples.includes(row.id)}
                                        onSelect={() => handleSelectRow(row.id)}
                                        expId={data[0].experiment_id}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <PaginationBar
                    perPage={perPage}
                    setPerPage={setPerPage}
                    page={page}
                    setPage={setPage}
                    totalPages={Math.ceil(displayedRows?.length / perPage)}
                />
            </Box>
        </>
    );
};

export default ExpandableTable;
